"use client"

import Image from "next/image";
import {X} from "lucide-react";
import React from "react";
import {Button} from "@/components/ui/button";
import type {AttachmentInfo} from "@/hooks/use-unified-file-upload";
import {getAttachmentIcon} from "@/app/components/attachmentIcon";
import {AttachmentType} from "@/pb/api/devplan/types/attachments";

export type {AttachmentInfo}

export interface FilePreviewListProps {
  /** Image URLs to display as thumbnails */
  imageUrls?: string[];
  /** Document attachments to display as file icons */
  attachments?: AttachmentInfo[];
  /** Callback when an image is deleted (by index) */
  onDeleteImage?: (index: number) => void;
  /** Callback when an attachment is deleted (by id) */
  onDeleteAttachment?: (id: string) => void;
}

export function FilePreviewList({
  imageUrls = [],
  attachments = [],
  onDeleteImage,
  onDeleteAttachment,
}: FilePreviewListProps) {
  if (imageUrls.length === 0 && attachments.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {/* Image previews */}
      {imageUrls.map((url, index) => (
        <div key={`img-${url}-${index}`} className="relative inline-block m-1">
          <a href={url} target="_blank" className="cursor-pointer">
            <Image src={url} className="rounded-lg" alt="image" height={50} width={50} unoptimized/>
          </a>
          {onDeleteImage && (
            <Button
              type="button"
              onClick={() => onDeleteImage(index)}
              aria-label={`Remove image ${index + 1}`}
              variant="ghost"
              size="icon"
              className="absolute end-1 top-1 translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[#f4f4f4] bg-black p-[2px] text-white dark:border-token-bg-tertiary dark:bg-white dark:text-black hover:bg-black/80 dark:hover:bg-white/80 h-auto w-auto"
            >
              <X className="size-3" aria-hidden="true"/>
            </Button>
          )}
        </div>
      ))}

      {/* Document previews */}
      {attachments.map((attachment) => (
        <div key={`doc-${attachment.id}`} className="relative inline-block m-1">
          <div className="flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-sm">
            <span className="[&_svg]:size-4">{getAttachmentIcon(AttachmentType.DOCUMENT, attachment.fileName)}</span>
            <span className="max-w-[150px] truncate text-foreground">{attachment.fileName}</span>
          </div>
          {onDeleteAttachment && (
            <Button
              type="button"
              onClick={() => onDeleteAttachment(attachment.id)}
              aria-label={`Remove ${attachment.fileName}`}
              variant="ghost"
              size="icon"
              className="absolute end-1 top-1 translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[#f4f4f4] bg-black p-[2px] text-white dark:border-token-bg-tertiary dark:bg-white dark:text-black hover:bg-black/80 dark:hover:bg-white/80 h-auto w-auto"
            >
              <X className="size-3" aria-hidden="true"/>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
